import React, { useEffect, useRef, useState } from 'react';
import { Box, Grid, IconButton, Typography } from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import useBreakpoints from '../../../common/hooks/useBreakpoints';

/**
 * Layout unificado para las cards del preview (habitaciones, hoteles, vuelos):
 *
 * - 1 o 2 items: se muestran COMPLETOS — lado a lado en desktop, apilados en
 *   mobile. Cero interacción necesaria.
 * - 3 o más: carrusel horizontal, pero con controles explícitos — flechas
 *   clickeables, contador "X de N" y scroll-snap. Regla para el público
 *   30-70: nada de gestos por descubrir; si hay contenido fuera de pantalla,
 *   tiene que estar señalizado y accesible con un click.
 * - `wrapOnDesktop` (hoteles y vuelos): en desktop, en vez de carrusel, una
 *   grilla que envuelve mostrando TODAS las opciones juntas — para elegir
 *   entre opciones, compararlas de un vistazo le gana a paginarlas.
 */

interface CarouselItemWidth {
	xs: number | string;
	sm?: number | string;
	md: number | string;
}

interface Props {
	children: React.ReactNode[];
	/** Sustantivo del contador ("Habitación 2 de 5", "Hotel 1 de 3"). */
	itemLabel?: string;
	/** Ancho del slot de cada card (en carrusel y en el wrap de desktop). */
	itemWidth?: CarouselItemWidth;
	/** Desktop: grilla envolvente con todo visible en vez de carrusel. */
	wrapOnDesktop?: boolean;
}

const DEFAULT_ITEM_WIDTH: CarouselItemWidth = { xs: '86%', sm: 340, md: 360 };

const ARROW_SX = {
	backgroundColor: 'rgba(49, 25, 75, 0.06)',
	color: 'primary.main',
	'&.Mui-disabled': { backgroundColor: 'rgba(49, 25, 75, 0.03)' },
};

const toCssWidth = (value: number | string) => (typeof value === 'number' ? `${value}px` : value);

export const CardsCarousel = ({
	children,
	itemLabel = 'Opción',
	itemWidth = DEFAULT_ITEM_WIDTH,
	wrapOnDesktop = false,
}: Props): JSX.Element => {
	const scrollRef = useRef<HTMLDivElement | null>(null);
	const [activeIndex, setActiveIndex] = useState(0);
	const { mediumDown } = useBreakpoints();
	const count = children.length;

	// Rendimiento del scroll (importante en Android de gama media): el handler
	// de scroll NO puede leer layout por cada card ni re-renderizar en cada
	// frame. Por eso: offsets cacheados, un cálculo por frame (rAF) y setState
	// sólo cuando el índice cambia de verdad.
	const offsetsRef = useRef<number[] | null>(null);
	const rafRef = useRef<number | null>(null);
	const activeRef = useRef(0);

	useEffect(() => {
		const invalidate = () => {
			offsetsRef.current = null;
		};
		window.addEventListener('resize', invalidate);
		return () => {
			window.removeEventListener('resize', invalidate);
			if (rafRef.current != null) cancelAnimationFrame(rafRef.current);
		};
	}, []);

	// Los offsets cambian si cambia la cantidad de items (otro vuelo elegido).
	useEffect(() => {
		offsetsRef.current = null;
	}, [count]);

	// Desktop con wrapOnDesktop: todas las opciones visibles, envolviendo.
	if (wrapOnDesktop && !mediumDown) {
		return (
			<Box display="flex" flexWrap="wrap" justifyContent="center" gap={1.5} pb={1}>
				{children.map((child, index) => (
					<Box
						key={`card-slot-${index}`}
						sx={{
							flex: `0 0 ${toCssWidth(itemWidth.md)}`,
							maxWidth: '100%',
							display: 'flex',
							alignItems: 'stretch',
							'& > *': { width: '100%' },
						}}
					>
						{child}
					</Box>
				))}
			</Box>
		);
	}

	if (count <= 2) {
		return (
			<Grid container spacing={1.5} alignItems="stretch">
				{children.map((child, index) => (
					<Grid
						key={`card-slot-${index}`}
						item
						xs={12}
						md={count > 1 ? 6 : 12}
						sx={{ display: 'flex', '& > *': { width: '100%' } }}
					>
						{child}
					</Grid>
				))}
			</Grid>
		);
	}

	// Una sola lectura de layout por generación (se invalida en resize / cambio
	// de cantidad), en vez de una por card por evento de scroll.
	const getOffsets = (): number[] => {
		const container = scrollRef.current;
		if (!container) return [];
		if (!offsetsRef.current || offsetsRef.current.length !== container.children.length) {
			offsetsRef.current = Array.from(container.children).map((child) => (child as HTMLElement).offsetLeft);
		}
		return offsetsRef.current;
	};

	const scrollToIndex = (index: number) => {
		const container = scrollRef.current;
		const offsets = getOffsets();
		if (!container || offsets[index] == null) return;
		// Scroll instantáneo, NO 'smooth': el suave depende de rAF y de soporte
		// del browser (Safari viejo no lo tiene) y falla silenciosamente — la
		// flecha que "no hace nada" es peor que un salto seco. El snap alinea.
		container.scrollTo({ left: offsets[index] });
		activeRef.current = index;
		setActiveIndex(index);
	};

	// El índice activo sale de la posición real de scroll, así el contador
	// también acompaña a quien desliza con el dedo en vez de usar las flechas.
	const handleScroll = () => {
		// Un cálculo por frame como máximo: los eventos de scroll llegan mucho
		// más seguido que los frames y no tiene sentido recalcular de más.
		if (rafRef.current != null) return;
		rafRef.current = requestAnimationFrame(() => {
			rafRef.current = null;
			const container = scrollRef.current;
			if (!container) return;
			const offsets = getOffsets();
			let nearest = 0;
			let nearestDistance = Number.POSITIVE_INFINITY;
			for (let index = 0; index < offsets.length; index++) {
				const distance = Math.abs(offsets[index] - container.scrollLeft);
				if (distance < nearestDistance) {
					nearestDistance = distance;
					nearest = index;
				}
			}
			// Sólo re-renderizamos si el item visible cambió: antes esto corría en
			// cada frame del arrastre y volvía a montar todas las cards.
			if (nearest !== activeRef.current) {
				activeRef.current = nearest;
				setActiveIndex(nearest);
			}
		});
	};

	return (
		<Box>
			{/* Al imprimir (el PDF que manda el asesor) no hay scroll: las flechas y
			    el contador no sirven y el carrusel se despliega — ver abajo. */}
			<Box
				display="flex"
				alignItems="center"
				justifyContent="space-between"
				gap={1}
				mb={0.75}
				sx={{ '@media print': { display: 'none' } }}
			>
				<Typography variant="body2" fontWeight={800} color="primary">
					{itemLabel} {activeIndex + 1} de {count}
				</Typography>
				<Box display="flex" gap={0.75}>
					<IconButton
						size="small"
						sx={ARROW_SX}
						onClick={() => scrollToIndex(Math.max(0, activeIndex - 1))}
						disabled={activeIndex === 0}
						aria-label={`${itemLabel} anterior`}
					>
						<ChevronLeftIcon fontSize="small" />
					</IconButton>
					<IconButton
						size="small"
						sx={ARROW_SX}
						onClick={() => scrollToIndex(Math.min(count - 1, activeIndex + 1))}
						disabled={activeIndex === count - 1}
						aria-label={`${itemLabel} siguiente`}
					>
						<ChevronRightIcon fontSize="small" />
					</IconButton>
				</Box>
			</Box>
			<Box
				ref={scrollRef}
				onScroll={handleScroll}
				sx={{
					position: 'relative',
					display: 'flex',
					gap: 1.5,
					overflowX: 'auto',
					scrollSnapType: 'x proximity',
					pb: 1,
					// Scrollbar finita pero visible en desktop: una pista más de
					// que hay contenido a la derecha.
					'&::-webkit-scrollbar': { height: 6 },
					'&::-webkit-scrollbar-thumb': { backgroundColor: 'rgba(49, 25, 75, 0.18)', borderRadius: '3px' },
					// En papel el overflow se recorta: lo que quedaba fuera de pantalla
					// simplemente no se imprimía. Desplegamos todo en filas.
					'@media print': {
						overflowX: 'visible',
						flexWrap: 'wrap',
						'& > *': { width: '48% !important', flex: '0 0 48% !important' },
					},
				}}
			>
				{children.map((child, index) => (
					<Box
						key={`card-slide-${index}`}
						sx={{
							flex: '0 0 auto',
							// La card no ocupa el 100% para que la siguiente asome
							// (peek): la pista visual clásica de "hay más".
							width: { xs: itemWidth.xs, sm: itemWidth.sm ?? itemWidth.xs, md: itemWidth.md },
							scrollSnapAlign: 'start',
							display: 'flex',
							'& > *': { width: '100%' },
						}}
					>
						{child}
					</Box>
				))}
			</Box>
		</Box>
	);
};

/** Alias con la etiqueta de habitaciones, para los tres usos del presupuesto. */
export const RoomCardsCarousel = (props: Omit<Props, 'itemLabel'>): JSX.Element => (
	<CardsCarousel {...props} itemLabel="Habitación" />
);

export default CardsCarousel;
